import { useState } from 'react'
import {
  PATTERN_COLLAPSED_TEMPLATES,
  PATTERN_EXPLAINERS,
  PATTERN_REMEDIATION,
  formatCollapsed,
} from '../../lib/pattern-catalog-schemas'
import { RHYTHM_STYLE } from '../../lib/rhythm-style'
import { PATTERN_WINDOW_DAYS, windowPeriod } from '../../lib/pattern-window'
import { RhythmInfoTooltip } from './RhythmInfoTooltip'
import { Skeleton } from '../primitives/Skeleton'
import type {
  HealthBarBand,
  HealthBarData,
  WellbeingNarrative,
} from '../../api/health-bar'

// ── Band tokens — all colours reference CSS vars from tokens.css ─────────────
// Low uses the pear palette (var(--pear) = #CDDE00), matching Lumen BAND.low.
// Policy v1.0 §2.3: three bands with friendly copy ("no risk jargon" per HR).

interface BandTokens {
  chipLabel: string    // HEALTHY | WARNING | CONCERNING
  dotColor: string     // ring stroke + chip dot
  chipBg: string       // chip + streak pill background
  chipBorder: string   // chip + streak pill border
  statBg: string       // stat block background
  statBorder: string   // stat block border
  accentFrom: string   // top-bar gradient start
  accentTo: string     // top-bar gradient end
  headline: string     // §2.3 headline
  sub: string          // §2.3 sub-line
  nudge: string        // ILMU footer nudge
}

const BAND: Record<HealthBarBand, BandTokens> = {
  low: {
    chipLabel: 'HEALTHY',
    dotColor: 'var(--pear)',
    chipBg: 'var(--pear-tint-2)',
    chipBorder: 'var(--pear-tint)',
    statBg: 'var(--pear-tint-2)',
    statBorder: 'var(--pear-tint)',
    accentFrom: 'var(--pear)',
    accentTo: 'var(--pear-tint)',
    headline: 'Your account is in great shape.',
    sub: 'Your leave snapshot shows a steady, balanced pattern overall.',
    nudge: 'Mid-year reminder — plan your remaining days early.',
  },
  medium: {
    chipLabel: 'WARNING',
    dotColor: 'var(--warning)',
    chipBg: 'var(--warning-bg)',
    chipBorder: 'var(--warning-line)',
    statBg: 'var(--warning-bg)',
    statBorder: 'var(--warning-line)',
    accentFrom: 'var(--warning)',
    accentTo: 'var(--warning-bg)',
    headline: 'A pattern or two to be aware of.',
    sub: "Take a moment to review what's flagged.",
    nudge: 'Consider a short break to reset the cadence.',
  },
  high: {
    chipLabel: 'CONCERNING',
    dotColor: 'var(--danger)',
    chipBg: 'var(--danger-bg)',
    chipBorder: 'var(--danger-line)',
    statBg: 'var(--danger-bg)',
    statBorder: 'var(--danger-line)',
    accentFrom: 'var(--danger)',
    accentTo: 'var(--danger-bg)',
    headline: 'Multiple patterns — chat with your manager.',
    sub: 'Time to plan a reset — the details are below.',
    nudge: 'Reach out to your manager. They can help plan a better cadence.',
  },
}

// Approver-POV overrides for headline / sub / nudge. Same band visuals;
// only the wording is reframed so a manager / HOD / HR / Director reviewing
// the requester reads decision-oriented copy rather than the self-POV
// "your account / chat with your manager" lines.
type AudienceCopy = Pick<BandTokens, 'headline' | 'sub' | 'nudge'>

const APPROVER_BAND: Record<HealthBarBand, AudienceCopy> = {
  low: {
    headline: 'Wellbeing signal is healthy.',
    sub: 'No active patterns flagged for this requester.',
    nudge: 'No wellbeing concerns to factor into this decision.',
  },
  medium: {
    headline: 'A pattern or two worth noting.',
    sub: 'Review what is flagged before deciding.',
    nudge: 'A brief check-in with the requester is recommended.',
  },
  high: {
    headline: 'Multiple patterns flagged — review carefully.',
    sub: 'Worth a conversation with the requester about cadence.',
    nudge: 'A wellbeing check-in is recommended before further requests.',
  },
}


// ── Helpers ───────────────────────────────────────────────────────────────────

/** Zero-patterns copy — exported so tests assert the exact string.
 *  Kept in sync with the `sub` field of the `low` band above (the only
 *  place this copy renders); commit f13b420e shortened the render to
 *  drop "in the background" but missed the constant. */
export const HEALTH_BAR_ZERO_PATTERNS_COPY =
  'Your leave snapshot shows a steady, balanced pattern overall.'

/** Friendly label: "frequent_sick_leave" → "Frequent sick leave". */
function humanisePatternCode(code: string): string {
  const words = code.split('_').filter(Boolean)
  if (words.length === 0) return code
  const first = words[0] ?? ''
  return [first.charAt(0).toUpperCase() + first.slice(1), ...words.slice(1)].join(' ')
}

/** Plain-language explainer from the pattern catalogue's `detects` field. */
function explainerFor(code: string): string {
  const entry = PATTERN_EXPLAINERS[code]
  return entry ? entry.detects : `Pattern: ${humanisePatternCode(code)}.`
}

/** Format a metric value: whole days stay whole (7), half-days keep one
 *  decimal (5.5). Percentages arrive pre-rounded from the backend. */
function fmtMetric(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

/**
 * Collapsed-card line for a pattern. When live `metrics` exist for a pattern
 * with a dynamic template (policy §2.3 "personal data point"), render the
 * substituted sentence; otherwise fall back to the static `detects` explainer.
 * `{month}` and `{period}` (window wording, "this year" when clamped at 1 Jan)
 * are supplied client-side from the current date.
 */
function collapsedMessageFor(code: string, metrics?: Record<string, number>): string {
  const template = PATTERN_COLLAPSED_TEMPLATES[code]
  if (!template || !metrics) return explainerFor(code)
  const windowDays = PATTERN_WINDOW_DAYS[code]
  const vars: Record<string, string | number> = {
    month: currentMonthName(),
    ...(windowDays !== undefined ? { period: windowPeriod(windowDays) } : {}),
  }
  for (const [key, value] of Object.entries(metrics)) {
    vars[key] = fmtMetric(value)
  }
  return formatCollapsed(template, vars)
}

/** Live current month, e.g. "July" — feeds the high_early_utilisation line. */
function currentMonthName(): string {
  return new Date().toLocaleString('en', { month: 'long' })
}

// ── Props ────────────────────────────────────────────────────────────────────

/**
 * Who is reading this card.
 *  - 'self'     → the employee whose account this is (home lobby, own leave detail)
 *  - 'approver' → a manager / HOD / HR / Director reviewing the requester
 *
 * Only the wording (headline, sub, nudge, remediation, "What you can do"
 * label) changes. The band, score, ring, and pattern list are identical.
 */
export type HealthBarAudience = 'self' | 'approver'

export interface HealthBarCardProps {
  health: HealthBarData
  /** Default 'self'. Pass 'approver' on the leave detail when the viewer
   *  is not the leave owner — switches headline / sub / remediation copy
   *  to a manager-perspective phrasing. */
  audience?: HealthBarAudience
  /** Optional ILMU-generated narrative (GET /api/v1/me/wellbeing-narrative).
   *  When present it replaces the deterministic band headline/sub. The band /
   *  score verdict is never affected — ILMU only narrates. Restored post-SG#1
   *  behind the `ilmuWellbeingNarrative` flag. */
  narrative?: WellbeingNarrative
  /** True while the narrative request is in flight (no data yet). When set,
   *  the greeting shows a skeleton instead of the deterministic fallback so
   *  the copy doesn't flash-then-swap. */
  narrativeLoading?: boolean
}

// ── HealthBarCard ─────────────────────────────────────────────────────────────
//
// Lumen WellbeingSidebar parity (Policy v1.0 §2.3). Top→bottom:
//   1. 2px accent bar  2. Eyebrow + chip  3. Headline + sub
//   4. Stat block (ring left, score+streak right)
//   5. Active patterns / empty-state  6. Forfeit nudge (flag-gated)
//   7. ILMU footer (marginTop: auto → pinned to bottom)
//
// height: 100% + flex-column stretches to match adjacent BalancesCard.

export function HealthBarCard({
  health,
  audience = 'self',
  narrative,
  narrativeLoading = false,
}: HealthBarCardProps): JSX.Element {
  const band = health.band
  const b = BAND[band]
  const isApprover = audience === 'approver'
  const copy: AudienceCopy = isApprover ? APPROVER_BAND[band] : b
  const hasPatterns = health.contributingPatterns.length > 0
  const patternCount = health.contributingPatterns.length
  const r = RHYTHM_STYLE[band]
  // Accordion: at most one pattern row expanded at a time.
  const [openPattern, setOpenPattern] = useState<string | null>(null)

  // Ring geometry — a solid colour ring (state, not a progress arc).
  const SZ = 104, SW = 9
  const rr = (SZ - SW) / 2

  return (
    <div
      data-testid="my-health-bar-card"
      data-band={band}
      style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 14, padding: '18px 18px 16px',
        fontFamily: 'var(--font-sans)',
        display: 'flex', flexDirection: 'column',
        height: '100%', boxSizing: 'border-box',
      }}
    >
      {/* Eyebrow row — just the label; the state now reads from the ring. */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 10.5, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 700 }}>
          Leave rhythm
        </div>
      </div>

      {/* Ring (left) + Headline + sub (right). Ring is a solid state ring with
         * the state icon + label at its centre (no percentage). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
        <div style={{ position: 'relative', width: SZ, height: SZ, flexShrink: 0 }}>
          <svg width={SZ} height={SZ} aria-hidden="true">
            <circle cx={SZ / 2} cy={SZ / 2} r={rr} fill={r.fill} />
            <circle cx={SZ / 2} cy={SZ / 2} r={rr} fill="none" stroke={r.ring} strokeWidth={SW} />
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
            <div aria-hidden="true" style={{ fontSize: r.iconSize, fontWeight: 800, color: r.ring, lineHeight: 1 }}>
              {r.icon}
            </div>
            <div data-testid="my-health-bar-state" style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.07em', color: r.ring, textTransform: 'uppercase' }}>
              {r.label}
            </div>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Greeting + summary from the wellbeing-narrative call (restored
              post-SG#1, flag-gated). Three states: (1) narrative present →
              show it; (2) still loading → skeleton; (3) not loading and no
              narrative (AI failed / flag off) → the deterministic band copy. */}
          {narrative ? (
            <>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', letterSpacing: '-.01em', lineHeight: 1.3, marginBottom: 5 }}>
                {narrative.headline}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.45 }}>
                {narrative.body}
              </div>
            </>
          ) : narrativeLoading ? (
            <div data-testid="my-health-bar-narrative-loading" aria-busy="true" aria-label="Loading your leave summary">
              <Skeleton height={13} width="62%" radius={4} />
              <div style={{ marginTop: 7, display: 'flex', flexDirection: 'column', gap: 5 }}>
                <Skeleton height={10} radius={3} />
                <Skeleton height={10} width="88%" radius={3} />
              </div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', letterSpacing: '-.01em', lineHeight: 1.3, marginBottom: 5 }}>
                {copy.headline}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.45 }}>
                {hasPatterns
                  ? `${patternCount} active pattern${patternCount === 1 ? '' : 's'} detected.`
                  : HEALTH_BAR_ZERO_PATTERNS_COPY}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Active patterns — bordered, tap-to-expand rows (prototype). Empty
          state shows a soft confirmation box in the band's tint. */}
      {hasPatterns ? (
        <div style={{ marginTop: 2, marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700, marginBottom: 8 }}>
            Active patterns
          </div>
          <div data-testid="my-health-bar-patterns" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {health.contributingPatterns.map((code) => (
              <PatternRow
                key={code}
                code={code}
                metrics={health.patternMetrics?.[code]}
                dotColor={r.ring}
                open={openPattern === code}
                onToggle={() => setOpenPattern((cur) => (cur === code ? null : code))}
              />
            ))}
          </div>
        </div>
      ) : (
        <div
          data-testid="my-health-bar-empty"
          style={{
            marginTop: 2, marginBottom: 12, padding: '10px 14px',
            borderRadius: 8, background: r.fill, color: r.chipText,
            fontSize: 12, lineHeight: 1.5,
          }}
        >
          ✓ No patterns flagged this period.
        </div>
      )}

      {/* Bottom link — a text affordance opening the pattern-legend tooltip.
         * marginTop: auto pins it to the card's bottom edge. */}
      <div style={{ marginTop: 'auto', paddingTop: 10, borderTop: '1px solid var(--border)' }}>
        <RhythmInfoTooltip />
      </div>

    </div>
  )
}

// ── PatternRow — one bordered, tap-to-expand row in the Active patterns list ──
// Collapsed: dot + title + one-line description + chevron. Expanded: reveals
// the remediation nudge (💡). Matches the redesign prototype.

interface PatternRowProps {
  code: string
  /** Live per-pattern values (e.g. { sick_days: 7 }) for the collapsed copy.
   *  Undefined → the row falls back to the static explainer text. */
  metrics?: Record<string, number> | undefined
  /** Ring/band accent used for the leading dot + the 💡 glyph. */
  dotColor: string
  /** Controlled by the parent so only one row is open at a time. */
  open: boolean
  onToggle: () => void
}

function PatternRow({ code, metrics, dotColor, open, onToggle }: PatternRowProps): JSX.Element {
  const title = humanisePatternCode(code)
  const message = collapsedMessageFor(code, metrics)
  const nudge = PATTERN_REMEDIATION[code] ?? PATTERN_EXPLAINERS[code]?.advice ?? ''
  return (
    <button
      type="button"
      data-pattern-code={code}
      aria-expanded={open}
      onClick={onToggle}
      style={{
        textAlign: 'left', width: '100%', display: 'block',
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 8, padding: '10px 12px', cursor: 'pointer',
        color: 'inherit', fontFamily: 'inherit',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
        <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor, flexShrink: 0, marginTop: 5 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2, lineHeight: 1.4 }}>{message}</div>
        </div>
        <span
          aria-hidden="true"
          style={{
            fontSize: 12, color: 'var(--text-3)', flexShrink: 0, marginTop: 1,
            transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 120ms ease',
          }}
        >
          ↓
        </span>
      </div>
      {open && nudge ? (
        <div style={{ marginTop: 9, paddingTop: 9, borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6 }}>
          <span style={{ color: dotColor }}>💡 </span>
          {nudge}
        </div>
      ) : null}
    </button>
  )
}
